/**
 * UnplacedAircraftDialog Component
 * Displays decision logs for unplaced aircraft at a specific time bucket
 */

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { bucketToTimeString } from "../bitmap";
import { LogEntry, ParkingRequest } from "../types";
import { DecisionLogDisplay } from "./DecisionLogDisplay";

interface UnplacedAircraftDialogProps {
  open: boolean;
  onClose: () => void;
  unplacedAircraft: ParkingRequest[];
  bucket: number;
  log: LogEntry[];
  spotIdToName?: Record<string, string>;
  startTime?: Date;
}

export const UnplacedAircraftDialog: React.FC<UnplacedAircraftDialogProps> =
  React.memo(
    ({
      open,
      onClose,
      unplacedAircraft,
      bucket,
      log,
      spotIdToName = {},
      startTime = new Date(new Date().setHours(0, 0, 0, 0)),
    }) => {
      const { t } = useTranslation();

      return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="h6" component="div">
                  {t('screens.ramp.autoPark.unplacedAircraftDialog.title', {
                    time: bucketToTimeString(bucket, startTime)
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('screens.ramp.autoPark.unplacedAircraftDialog.subtitle', {
                    count: unplacedAircraft.length
                  })}
                </Typography>
              </Box>
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label={t('screens.ramp.autoPark.unplacedAircraftDialog.closeLabel')}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ maxHeight: "70vh", overflow: "auto" }}>
              {unplacedAircraft.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  {t('screens.ramp.autoPark.unplacedAircraftDialog.noAircraft')}
                </Typography>
              ) : (
                unplacedAircraft.map((aircraft) => (
                  <Box
                    key={aircraft.id}
                    sx={{
                      mb: 3,
                      pb: 3,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": {
                        borderBottom: "none",
                        mb: 0,
                        pb: 0,
                      },
                    }}
                  >
                    <DecisionLogDisplay
                      aircraft={aircraft}
                      log={log}
                      spotIdToName={spotIdToName}
                    />
                  </Box>
                ))
              )}
            </Box>
          </DialogContent>
        </Dialog>
      );
    }
  );
